(() => {
  const GIFT_MARKER = '_kvd_box_fill_gift';
  const OFFER_VARIANT = '_kvd_offer_variant_id';
  const REQUIRED_QUANTITY = '_kvd_required_quantity';
  const BASELINE_PACKAGES = '_kvd_baseline_packages';
  const CAPACITY_GRAMS = '_kvd_capacity_grams';
  const OFFER_TOKEN = '_kvd_offer_token';
  const FLASH_KEY = 'kvd-box-filler-flash';
  const EXACT_LIMITS = Object.freeze({ maxItems: 48, maxNodes: 75000, maxMs: 45 });

  /** Best-fit decreasing: put every next-heaviest unit in the fullest bin it still fits. */
  function bestFitDecreasing(weights, capacity) {
    const clean = weights.map(Number);
    if (!Number.isFinite(capacity) || capacity <= 0 || clean.some((weight) => !Number.isFinite(weight) || weight <= 0 || weight > capacity)) {
      return { valid: false, count: 0, used: [], remaining: [] };
    }

    const used = [];
    clean.sort((a, b) => b - a).forEach((weight) => {
      let bestIndex = -1;
      let leastSpaceAfter = Number.POSITIVE_INFINITY;

      used.forEach((binWeight, index) => {
        const spaceAfter = capacity - binWeight - weight;
        if (spaceAfter >= 0 && spaceAfter < leastSpaceAfter) {
          bestIndex = index;
          leastSpaceAfter = spaceAfter;
        }
      });

      if (bestIndex === -1) used.push(weight);
      else used[bestIndex] += weight;
    });

    return {
      valid: true,
      count: used.length,
      used,
      remaining: used.map((weight) => capacity - weight),
    };
  }

  function nowMs() {
    return window.performance?.now ? window.performance.now() : Date.now();
  }

  function createSearchBudget(overrides = {}) {
    return {
      startedAt: nowMs(),
      nodes: 0,
      aborted: false,
      maxNodes: Number(overrides.maxNodes || EXACT_LIMITS.maxNodes),
      maxMs: Number(overrides.maxMs || EXACT_LIMITS.maxMs),
    };
  }

  /**
   * Exact feasibility for a fixed number of bins. BFD is used only as a proven
   * feasible upper bound; a branch-and-bound search decides every harder case.
   * `valid:false` means fail closed because input or a resource cap was hit.
   */
  function exactCanPack(weights, capacity, binCount, sharedBudget = null) {
    const clean = weights.map(Number).sort((a, b) => b - a);
    if (!Number.isFinite(capacity) || capacity <= 0
      || !Number.isInteger(binCount) || binCount < 0
      || clean.some((weight) => !Number.isFinite(weight) || weight <= 0 || weight > capacity)
      || clean.length > EXACT_LIMITS.maxItems) {
      return { valid: false, exact: false, feasible: false, reason: 'input-limit' };
    }
    if (!clean.length) return { valid: true, exact: true, feasible: binCount >= 0 };
    if (binCount === 0) return { valid: true, exact: true, feasible: false };

    const total = clean.reduce((sum, weight) => sum + weight, 0);
    if (total > binCount * capacity) return { valid: true, exact: true, feasible: false };

    // A BFD result within the requested bin count is a concrete packing proof.
    if (bestFitDecreasing(clean, capacity).count <= binCount) {
      return { valid: true, exact: true, feasible: true };
    }

    const budget = sharedBudget || createSearchBudget();
    if (budget.aborted) return { valid: false, exact: false, feasible: false, reason: 'search-limit' };

    const bins = Array(binCount).fill(0);
    const memo = new Set();
    bins[0] = clean[0];

    function search(index) {
      budget.nodes += 1;
      if (budget.nodes > budget.maxNodes
        || ((budget.nodes & 255) === 0 && nowMs() - budget.startedAt > budget.maxMs)) {
        budget.aborted = true;
        return null;
      }
      if (index >= clean.length) return true;

      const canonicalBins = [...bins].sort((a, b) => b - a);
      const memoKey = `${index}|${canonicalBins.join(',')}`;
      if (memo.has(memoKey)) return false;

      const weight = clean[index];
      const candidates = bins
        .map((used, binIndex) => ({ binIndex, used, spaceAfter: capacity - used - weight }))
        .filter((candidate) => candidate.spaceAfter >= 0)
        .sort((a, b) => a.spaceAfter - b.spaceAfter);
      const seenLoads = new Set();

      for (const candidate of candidates) {
        if (seenLoads.has(candidate.used)) continue;
        seenLoads.add(candidate.used);
        bins[candidate.binIndex] += weight;
        const result = search(index + 1);
        bins[candidate.binIndex] -= weight;
        if (result === true) return true;
        if (result === null) return null;
        // All empty bins are symmetric.
        if (candidate.used === 0) break;
      }

      memo.add(memoKey);
      return false;
    }

    const result = search(1);
    if (result === null) return { valid: false, exact: false, feasible: false, reason: 'search-limit' };
    return { valid: true, exact: true, feasible: result };
  }

  function exactMinimumBins(weights, capacity) {
    const clean = weights.map(Number);
    if (!Number.isFinite(capacity) || capacity <= 0
      || clean.some((weight) => !Number.isFinite(weight) || weight <= 0 || weight > capacity)
      || clean.length > EXACT_LIMITS.maxItems) {
      return { valid: false, exact: false, count: 0, reason: 'input-limit' };
    }
    if (!clean.length) return { valid: true, exact: true, count: 0 };

    const total = clean.reduce((sum, weight) => sum + weight, 0);
    const lowerBound = Math.ceil(total / capacity);
    const upperBound = bestFitDecreasing(clean, capacity).count;
    if (lowerBound === upperBound) return { valid: true, exact: true, count: lowerBound };

    const budget = createSearchBudget();
    for (let binCount = lowerBound; binCount < upperBound; binCount += 1) {
      const result = exactCanPack(clean, capacity, binCount, budget);
      if (!result.valid) return { valid: false, exact: false, count: 0, reason: result.reason };
      if (result.feasible) return { valid: true, exact: true, count: binCount };
    }
    return { valid: true, exact: true, count: upperBound };
  }

  function isMarkedGift(item, giftVariantId) {
    return Number(item.variant_id) === giftVariantId && String(item.properties?.[GIFT_MARKER] || '') === '1';
  }

  function isPlainOneTimeLine(item) {
    return !item.selling_plan_allocation && Object.keys(item.properties || {}).length === 0;
  }

  function expandCartWeights(cart, giftVariantId, includeGift = true) {
    const weights = [];
    for (const item of cart.items || []) {
      const isGiftVariant = Number(item.variant_id) === giftVariantId;
      if (isGiftVariant && !includeGift) continue;

      const grams = Number(item.grams);
      const quantity = Number(item.quantity);
      if (!Number.isFinite(grams) || grams <= 0 || !Number.isInteger(quantity) || quantity < 0) return null;
      for (let i = 0; i < quantity; i += 1) weights.push(grams);
    }
    return weights;
  }

  function packageCart(cart, capacity, giftVariantId, includeGift = true) {
    const weights = expandCartWeights(cart, giftVariantId, includeGift);
    return weights ? exactMinimumBins(weights, capacity) : { valid: false, exact: false, count: 0, reason: 'invalid-weight' };
  }

  function packageBeforeQualifyingUnit(cart, capacity, giftVariantId, requiredVariantId) {
    const weights = [];
    let removedQualifyingUnit = false;

    for (const item of cart.items || []) {
      if (Number(item.variant_id) === giftVariantId) continue;
      const grams = Number(item.grams);
      let quantity = Number(item.quantity);
      if (!Number.isFinite(grams) || grams <= 0 || grams > capacity || !Number.isInteger(quantity) || quantity < 0) {
        return { valid: false, count: 0, used: [], remaining: [] };
      }
      if (!removedQualifyingUnit
        && Number(item.variant_id) === requiredVariantId
        && isPlainOneTimeLine(item)
        && quantity > 0) {
        quantity -= 1;
        removedQualifyingUnit = true;
      }
      for (let i = 0; i < quantity; i += 1) weights.push(grams);
    }

    if (!removedQualifyingUnit) return { valid: false, count: 0, used: [], remaining: [] };
    return exactMinimumBins(weights, capacity);
  }

  function createToken() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  async function jsonRequest(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', ...(options.headers || {}) },
      ...options,
    });
    if (!response.ok) {
      let detail = '';
      try { detail = (await response.json()).description || ''; } catch (_) { /* no-op */ }
      throw new Error(detail || `Cart request failed (${response.status})`);
    }
    return response.json();
  }

  const operationTokens = new Set();
  const lockedControls = new Set();
  const lockedControlState = new WeakMap();

  function asCheckoutControl(node) {
    const control = node?.closest?.('button, input, a');
    if (!control) return null;
    const href = control.getAttribute('href') || '';
    const formAction = control.getAttribute('formaction') || '';
    const checkoutHref = /\/(?:checkout|checkouts)(?:[/?#]|$)/i.test(`${href} ${formAction}`);
    const checkoutName = String(control.getAttribute('name') || '').toLowerCase() === 'checkout';
    const checkoutId = control.id === 'CartDrawer-Checkout';
    const checkoutData = control.hasAttribute('data-checkout') || /checkout/i.test(control.getAttribute('data-testid') || '');
    const cartSurface = control.closest('#CartDrawer, form[action*="/cart"]');
    return checkoutHref || (cartSurface && (checkoutName || checkoutId || checkoutData)) ? control : null;
  }

  function currentCheckoutControls() {
    const checkoutControls = [...document.querySelectorAll(
      'a[href*="/checkout"], a[href*="/checkouts"], [name="checkout"], #CartDrawer-Checkout, [data-checkout]'
    )]
      .map(asCheckoutControl)
      .filter(Boolean);
    const cartMutationControls = [...document.querySelectorAll(
      '#CartDrawer .quantity__button, #CartDrawer .quantity__input, #CartDrawer cart-remove-button button, '
      + 'form[action*="/cart"] [name="updates[]"], form[action*="/cart"] [name="update"]'
    )];
    return [...new Set([...checkoutControls, ...cartMutationControls])];
  }

  function syncCheckoutLock() {
    const locked = operationTokens.size > 0;
    if (locked) {
      currentCheckoutControls().forEach((control) => {
        if (!lockedControls.has(control)) {
          lockedControlState.set(control, {
            disabled: 'disabled' in control ? Boolean(control.disabled) : null,
            ariaDisabled: control.getAttribute('aria-disabled'),
            tabIndex: control.getAttribute('tabindex'),
          });
          lockedControls.add(control);
        }
        if ('disabled' in control) control.disabled = true;
        control.setAttribute('aria-disabled', 'true');
        control.setAttribute('aria-busy', 'true');
        if (control.tagName === 'A') control.setAttribute('tabindex', '-1');
      });
      return;
    }

    lockedControls.forEach((control) => {
      const state = lockedControlState.get(control);
      if (state) {
        if ('disabled' in control && state.disabled !== null) control.disabled = state.disabled;
        if (state.ariaDisabled === null) control.removeAttribute('aria-disabled');
        else control.setAttribute('aria-disabled', state.ariaDisabled);
        if (state.tabIndex === null) control.removeAttribute('tabindex');
        else control.setAttribute('tabindex', state.tabIndex);
      }
      control.removeAttribute('aria-busy');
      lockedControls.delete(control);
      lockedControlState.delete(control);
    });
  }

  function acquireOperationLock(label) {
    const token = Symbol(label);
    operationTokens.add(token);
    syncCheckoutLock();
    return token;
  }

  function releaseOperationLock(token) {
    operationTokens.delete(token);
    syncCheckoutLock();
  }

  function operationIsLocked() {
    return operationTokens.size > 0;
  }

  if (!customElements.get('kvd-box-filler')) {
    class KvdBoxFiller extends HTMLElement {
      connectedCallback() {
        if (this.initialized) {
          this.refresh();
          return;
        }
        this.initialized = true;
        this.capacity = Number(this.dataset.capacityGrams || 18000);
        this.giftVariantId = Number(this.dataset.giftVariantId);
        this.giftWeight = Number(this.dataset.giftWeightGrams || 40);
        this.giftCostCents = Number(this.dataset.giftCostCents || 97);
        this.minNetContributionCents = Number(this.dataset.minNetContributionCents || 100);
        try {
          const marginJson = JSON.parse(this.querySelector('[data-kvd-margin-meta]')?.textContent || '{"items":[]}');
          this.marginByVariantId = new Map((marginJson.items || []).map((item) => [
            Number(item.variantId),
            {
              rate: Number(item.marginRate),
              netContributionCents: item.netContributionCents === null || item.netContributionCents === undefined
                ? null
                : Number(item.netContributionCents),
              eligible: item.eligible === true,
            },
          ]));
        } catch (_) {
          this.marginByVariantId = new Map();
        }
        this.button = this.querySelector('[data-kvd-offer-button]');
        this.titleNode = this.querySelector('[data-kvd-offer-title]');
        this.messageNode = this.querySelector('[data-kvd-offer-message]');
        this.statusNode = this.querySelector('[data-kvd-offer-status]');
        this.imageNode = this.querySelector('[data-kvd-offer-image]');
        this.button?.addEventListener('click', () => this.acceptOffer());
        this.refresh();
      }

      get cartUrl() { return this.dataset.cartJsonUrl; }
      get changeUrl() { return this.dataset.cartChangeUrl; }
      get addUrl() { return this.dataset.cartAddUrl; }
      get updateUrl() { return this.dataset.cartUpdateUrl; }

      async getCart() {
        return jsonRequest(this.cartUrl, { method: 'GET', headers: { 'Content-Type': 'application/json' } });
      }

      findOffer(cart) {
        if (!this.giftVariantId || !this.giftWeight || (cart.items || []).some((item) => Number(item.variant_id) === this.giftVariantId)) return null;

        const base = packageCart(cart, this.capacity, this.giftVariantId, false);
        if (!base.valid || !base.exact || base.count < 1) return null;

        const baseWeights = expandCartWeights(cart, this.giftVariantId, false);
        const offerSearchBudget = createSearchBudget({ maxNodes: 120000, maxMs: 65 });
        const candidates = (cart.items || [])
          .filter((item) => Number(item.variant_id) !== this.giftVariantId)
          .filter((item) => Number(item.grams) > 0 && Number(item.grams) <= this.capacity)
          .filter((item) => Number(item.final_price) > 0)
          .filter(isPlainOneTimeLine)
          .filter((item) => this.marginByVariantId.get(Number(item.variant_id))?.eligible === true)
          .map((item) => {
            const simulation = exactCanPack(
              [...baseWeights, Number(item.grams), this.giftWeight],
              this.capacity,
              base.count,
              offerSearchBudget,
            );
            const marginMeta = this.marginByVariantId.get(Number(item.variant_id)) || {};
            const marginRate = marginMeta.rate ?? 0;
            // Margin tags are calculated on the selling price excluding 21% VAT.
            const estimatedGrossContribution = (Number(item.final_price) / 1.21) * marginRate;
            const estimatedNetContribution = Number.isFinite(marginMeta.netContributionCents)
              ? marginMeta.netContributionCents
              : estimatedGrossContribution - this.giftCostCents;
            return { item, simulation, marginRate, estimatedGrossContribution, estimatedNetContribution };
          })
          .filter(({ simulation }) => simulation.valid && simulation.exact && simulation.feasible)
          .filter(({ estimatedNetContribution }) => estimatedNetContribution >= this.minNetContributionCents)
          // The commercial objective is maximum extra product weight in the same parcels.
          .sort((a, b) => Number(b.item.grams) - Number(a.item.grams)
            || b.estimatedGrossContribution - a.estimatedGrossContribution
            || Number(b.item.final_price) - Number(a.item.final_price));

        if (!candidates.length) return null;
        return { ...candidates[0], base };
      }

      validateGift(cart) {
        const giftVariantLines = (cart.items || []).filter((item) => Number(item.variant_id) === this.giftVariantId);
        const markedGifts = giftVariantLines.filter((item) => isMarkedGift(item, this.giftVariantId));
        if (!giftVariantLines.length) return { hasGift: false, valid: true, giftLines: [] };
        if (giftVariantLines.length !== 1 || markedGifts.length !== 1 || Number(markedGifts[0].quantity) !== 1) {
          return { hasGift: true, valid: false, giftLines: giftVariantLines, reason: 'Cadeau ongeldig of meer dan één keer aanwezig.' };
        }

        const gift = markedGifts[0];
        const properties = gift.properties || {};
        const requiredVariantId = Number(properties[OFFER_VARIANT]);
        const requiredQuantity = Number(properties[REQUIRED_QUANTITY]);
        const baselinePackages = Number(properties[BASELINE_PACKAGES]);
        const recordedCapacity = Number(properties[CAPACITY_GRAMS]);
        const paidRequiredQuantity = (cart.items || [])
          .filter((item) => Number(item.variant_id) === requiredVariantId
            && Number(item.variant_id) !== this.giftVariantId
            && isPlainOneTimeLine(item))
          .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
        const currentWeights = expandCartWeights(cart, this.giftVariantId, true);
        const currentPacking = currentWeights
          ? exactMinimumBins(currentWeights, this.capacity)
          : { valid: false, exact: false, count: 0 };
        const packedBeforeQualifyingUnit = packageBeforeQualifyingUnit(
          cart,
          this.capacity,
          this.giftVariantId,
          requiredVariantId,
        );
        const giftIsFree = Number(gift.final_price) === 0 && Number(gift.final_line_price) === 0;

        const valid = Boolean(properties[OFFER_TOKEN])
          && requiredVariantId > 0
          && requiredQuantity > 0
          && paidRequiredQuantity >= requiredQuantity
          && baselinePackages > 0
          && recordedCapacity === this.capacity
          && currentPacking.valid
          && currentPacking.exact
          && currentPacking.count <= baselinePackages
          && packedBeforeQualifyingUnit.valid
          && packedBeforeQualifyingUnit.exact
          && packedBeforeQualifyingUnit.count <= baselinePackages
          && currentPacking.count === packedBeforeQualifyingUnit.count
          && giftIsFree;

        return {
          hasGift: true,
          valid,
          giftLines: giftVariantLines,
          reason: valid ? '' : (giftIsFree
            ? 'Door de gewijzigde winkelwagen past het cadeau niet meer in dezelfde zending.'
            : 'De snackkorting is niet als €0,00 bevestigd; het cadeau is verwijderd.'),
        };
      }

      async removeGiftLines(lines, reason = '') {
        const updates = {};
        lines.forEach((item) => { updates[item.key] = 0; });
        if (Object.keys(updates).length) await jsonRequest(this.updateUrl, { method: 'POST', body: JSON.stringify({ updates }) });
        if (reason) sessionStorage.setItem(FLASH_KEY, reason);
      }

      async refresh() {
        const operationToken = acquireOperationLock('refresh');
        try {
          const cart = await this.getCart();
          const giftState = this.validateGift(cart);
          if (giftState.hasGift && !giftState.valid) {
            await this.removeGiftLines(giftState.giftLines, giftState.reason);
            this.reloadDrawer();
            return;
          }

          if (giftState.hasGift) {
            this.renderEarned();
            return;
          }

          const offer = this.findOffer(cart);
          if (!offer) {
            const flash = sessionStorage.getItem(FLASH_KEY);
            if (flash) {
              sessionStorage.removeItem(FLASH_KEY);
              this.renderNotice(flash);
              return;
            }
            this.hidden = true;
            return;
          }
          this.renderOffer(offer);
        } catch (error) {
          console.error('[KVD box filler]', error);
          this.hidden = true;
        } finally {
          releaseOperationLock(operationToken);
        }
      }

      renderOffer(offer) {
        this.offer = offer;
        const item = offer.item;
        const boxLabel = offer.base.count === 1 ? 'dezelfde doos' : `dezelfde ${offer.base.count} dozen`;
        this.titleNode.textContent = `Nog 1× ${item.product_title} + gratis kattensnack`;
        this.messageNode.textContent = `Past in ${boxLabel}; er komt geen extra verzenddoos bij. Maximaal één cadeau per bestelling.`;
        this.imageNode.src = item.featured_image?.url || item.image || '';
        this.imageNode.alt = '';
        this.button.hidden = false;
        this.classList.remove('is-notice');
        this.classList.remove('is-earned');
        this.hidden = false;

        const flash = sessionStorage.getItem(FLASH_KEY);
        if (flash) {
          sessionStorage.removeItem(FLASH_KEY);
          this.statusNode.textContent = flash;
          this.statusNode.classList.remove('visually-hidden');
        } else {
          this.statusNode.textContent = '';
          this.statusNode.classList.add('visually-hidden');
        }
      }

      renderEarned() {
        this.offer = null;
        this.titleNode.textContent = 'Je gratis kattensnack is toegevoegd';
        this.messageNode.textContent = 'Het cadeau blijft gratis zolang de extra aankoop in hetzelfde aantal verzenddozen past.';
        this.imageNode.src = this.dataset.giftImage || '';
        this.button.hidden = true;
        this.classList.remove('is-notice');
        this.classList.add('is-earned');
        this.statusNode.textContent = '';
        this.statusNode.classList.add('visually-hidden');
        this.hidden = false;
      }

      renderNotice(message) {
        this.offer = null;
        this.titleNode.textContent = 'Winkelwagen bijgewerkt';
        this.messageNode.textContent = message;
        this.statusNode.textContent = '';
        this.statusNode.classList.add('visually-hidden');
        this.button.hidden = true;
        this.classList.remove('is-earned');
        this.classList.add('is-notice');
        this.hidden = false;
      }

      setBusy(isBusy, message = '') {
        if (this.button) {
          this.button.disabled = isBusy;
          this.button.textContent = isBusy ? 'Even verwerken…' : '+ 1 toevoegen & gratis snack';
        }
        if (message) {
          this.statusNode.textContent = message;
          this.statusNode.classList.remove('visually-hidden');
        }
      }

      async acceptOffer() {
        if (this.button?.disabled || !this.offer || operationIsLocked()) return;
        const operationToken = acquireOperationLock('accept-offer');
        this.setBusy(true);
        let changedItem = null;

        try {
          // Re-evaluate immediately before writing; quantities may have changed since rendering.
          const cart = await this.getCart();
          const freshOffer = this.findOffer(cart);
          if (!freshOffer || freshOffer.item.key !== this.offer.item.key) throw new Error('De winkelwagen is gewijzigd. De aanbieding wordt opnieuw berekend.');

          const baselinePackages = freshOffer.base.count;
          const item = freshOffer.item;
          const requiredQuantity = Number(item.quantity) + 1;
          changedItem = {
            variantId: Number(item.variant_id),
            originalQuantity: Number(item.quantity),
          };
          await jsonRequest(this.changeUrl, {
            method: 'POST',
            body: JSON.stringify({ id: item.key, quantity: requiredQuantity }),
          });

          await jsonRequest(this.addUrl, {
            method: 'POST',
            body: JSON.stringify({
              items: [{
                id: this.giftVariantId,
                quantity: 1,
                properties: {
                  [GIFT_MARKER]: '1',
                  [OFFER_VARIANT]: String(item.variant_id),
                  [REQUIRED_QUANTITY]: String(requiredQuantity),
                  [BASELINE_PACKAGES]: String(baselinePackages),
                  [CAPACITY_GRAMS]: String(this.capacity),
                  [OFFER_TOKEN]: createToken(),
                },
              }],
            }),
          });

          // A native automatic BXGY discount prices the hidden gift at zero. The hard
          // assertion below prevents a normal-price gift ever remaining in the cart.
          const finalCart = await this.getCart();
          const validation = this.validateGift(finalCart);
          if (!validation.valid) throw new Error(validation.reason || 'Het cadeau past niet meer in dezelfde zending.');
          const discountedGift = (finalCart.items || []).find((line) => isMarkedGift(line, this.giftVariantId));
          if (!discountedGift || Number(discountedGift.final_price) !== 0 || Number(discountedGift.final_line_price) !== 0) {
            throw new Error('De snackkorting is niet als €0,00 bevestigd; de wijziging is teruggedraaid.');
          }

          this.reloadDrawer();
        } catch (error) {
          // The action is deliberately transactional: never leave a paid/invalid gift or an accidental extra unit behind.
          try {
            const rollbackCart = await this.getCart();
            const giftLines = (rollbackCart.items || []).filter((item) => Number(item.variant_id) === this.giftVariantId);
            if (giftLines.length) await this.removeGiftLines(giftLines);
            if (changedItem) {
              // Discount applications can change a line key, so resolve the current key again.
              const cartAfterGiftRemoval = await this.getCart();
              const currentPaidLine = (cartAfterGiftRemoval.items || []).find((item) =>
                Number(item.variant_id) === changedItem.variantId
                && isPlainOneTimeLine(item));
              if (currentPaidLine && Number(currentPaidLine.quantity) > changedItem.originalQuantity) await jsonRequest(this.changeUrl, {
                method: 'POST',
                body: JSON.stringify({
                  id: currentPaidLine.key,
                  quantity: Math.max(0, Number(currentPaidLine.quantity) - 1),
                }),
              });
            }
          } catch (rollbackError) {
            console.error('[KVD box filler rollback]', rollbackError);
          }
          const message = error.message || 'Deze aanbieding kon niet worden toegevoegd. Probeer opnieuw.';
          sessionStorage.setItem(FLASH_KEY, message);
          this.setBusy(false, message);
          this.reloadDrawer();
        } finally {
          releaseOperationLock(operationToken);
        }
      }

      async prepareCheckout() {
        const cart = await this.getCart();
        const validation = this.validateGift(cart);
        if (validation.hasGift && !validation.valid) {
          await this.removeGiftLines(validation.giftLines, validation.reason);
          return { validGift: false, giftRemoved: true };
        }
        return { validGift: validation.hasGift && validation.valid, giftRemoved: false };
      }

      reloadDrawer() {
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.reload === 'function') drawer.reload();
        else window.location.reload();
      }
    }

    customElements.define('kvd-box-filler', KvdBoxFiller);
  }

  async function guardedCheckout(boxFiller, targetUrl) {
    if (!boxFiller || operationIsLocked()) return;
    const operationToken = acquireOperationLock('checkout-validation');
    try {
      const result = await boxFiller.prepareCheckout();
      if (result.giftRemoved) boxFiller.reloadDrawer();
      window.location.assign(targetUrl || boxFiller.dataset.checkoutUrl);
    } catch (error) {
      console.error('[KVD checkout gift validation]', error);
      boxFiller.setBusy(false, 'Controle van het cadeau lukte niet. Probeer nogmaals.');
    } finally {
      releaseOperationLock(operationToken);
    }
  }

  function interceptCheckout(event, explicitControl = null) {
    const control = explicitControl || asCheckoutControl(event.target);
    if (!control) return false;
    const boxFiller = document.querySelector('kvd-box-filler');
    if (!boxFiller && !operationIsLocked()) return false;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (operationIsLocked()) return true;

    const href = control.tagName === 'A' ? control.getAttribute('href') : '';
    guardedCheckout(boxFiller, href || boxFiller.dataset.checkoutUrl);
    return true;
  }

  // Capture checkout controls on both the drawer and cart forms. A submit without an
  // actual checkout submitter (for example Enter in a quantity input) is ignored.
  document.addEventListener('click', (event) => {
    const control = asCheckoutControl(event.target);
    if (!control) return;
    // Let native form submission run first so required fields and the optional
    // terms checkbox are validated. The submit listener below then guards checkout.
    if (!operationIsLocked() && control.form && ['BUTTON', 'INPUT'].includes(control.tagName)) return;
    interceptCheckout(event, control);
  }, true);

  document.addEventListener('submit', (event) => {
    const control = asCheckoutControl(event.submitter);
    if (!control) return;
    interceptCheckout(event, control);
  }, true);

  // Export pure helpers only for preview/console QA; does not expose cart mutation helpers.
  window.KvdBoxPacking = Object.freeze({ bestFitDecreasing, exactCanPack, exactMinimumBins });
})();
